import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Slider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  TextField,
  IconButton,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  Notifications,
  VolumeUp,
  VolumeOff,
  Folder,
  Star,
  Label,
  DoNotDisturb,
  PlayArrow,
  DesktopWindows,
  Inbox,
  Send,
  Drafts,
} from '@mui/icons-material';
import { useSettingsStore } from '../../stores/settingsStore';
import { testNotification, testSound, updateNotificationSettings } from '../../services/notificationService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface FolderNotification {
  id: string;
  name: string;
  icon: React.ReactNode;
  enabled: boolean;
  sound: boolean;
}

const NotificationSettings: React.FC = () => {
  const location = useLocation();
  const { settings, isLoading, isSaving, updateSettings } = useSettingsStore();

  // Derive initial tab from URL sub-route
  const initialTab = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/notification/desktop')) return 0;
    if (path.includes('/notification/priority')) return 1;
    if (path.includes('/notification/quiet'))   return 3;
    if (path.includes('/notification/mail'))     return 1;
    return 0;
  }, [location.pathname]);

  const [tabValue, setTabValue] = useState(initialTab);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  // Parse folder notifications from settings (stored as JSON string)
  const parseFolderNotifications = (): FolderNotification[] => {
    const defaults: FolderNotification[] = [
      { id: 'inbox', name: 'Inbox', icon: <Inbox />, enabled: true, sound: true },
      { id: 'starred', name: 'Starred', icon: <Star sx={{ color: 'warning.main' }} />, enabled: true, sound: true },
      { id: 'important', name: 'Important', icon: <Label sx={{ color: 'error.main' }} />, enabled: true, sound: false },
      { id: 'sent', name: 'Sent', icon: <Send />, enabled: false, sound: false },
      { id: 'drafts', name: 'Drafts', icon: <Drafts />, enabled: false, sound: false },
    ];
    if (!settings.folderNotifications) return defaults;
    try {
      const parsed = JSON.parse(settings.folderNotifications);
      return defaults.map(d => {
        const saved = parsed.find((p: any) => p.id === d.id);
        return saved ? { ...d, enabled: saved.enabled, sound: saved.sound } : d;
      });
    } catch {
      return defaults;
    }
  };

  const folderNotifications = parseFolderNotifications();

  // SettingsLayout already fetches settings

  const handleSettingChange = async (key: string, value: any) => {
    try {
      await updateSettings({ [key]: value });
      setSnackbar({ open: true, message: 'Settings saved', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    }
  };

  const handleFolderNotificationToggle = async (id: string, field: 'enabled' | 'sound') => {
    const updated = folderNotifications.map(f => f.id === id ? { ...f, [field]: !f[field] } : f);
    const toSave = updated.map(({ id, name, enabled, sound }) => ({ id, name, enabled, sound }));
    try {
      await updateSettings({ folderNotifications: JSON.stringify(toSave) });
      setSnackbar({ open: true, message: 'Folder notification updated', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save', severity: 'error' });
    }
  };

  const playTestSound = (soundId: string) => {
    if (soundId === 'none') {
      setSnackbar({ open: true, message: 'Silent mode - no sound', severity: 'info' });
      return;
    }
    testSound(soundId as 'default' | 'chime' | 'ding' | 'pop' | 'bell');
    setSnackbar({ open: true, message: `Playing: ${soundId}`, severity: 'info' });
  };

  const notificationSounds = [
    { id: 'default', name: 'Default' },
    { id: 'chime', name: 'Chime' },
    { id: 'ding', name: 'Ding' },
    { id: 'pop', name: 'Pop' },
    { id: 'bell', name: 'Bell' },
    { id: 'none', name: 'None (Silent)' },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={32} color="primary" />
      </Box>
    );
  }

  return (
    <Box>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={2000} 
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography variant="h5">Notifications</Typography>
        {isSaving && <CircularProgress size={16} color="primary" />}
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<DesktopWindows />} label="Desktop" iconPosition="start" />
        <Tab icon={<Folder />} label="Folders" iconPosition="start" />
        <Tab icon={<VolumeUp />} label="Sounds" iconPosition="start" />
        <Tab icon={<DoNotDisturb />} label="Quiet Hours" iconPosition="start" />
      </Tabs>

      {/* Desktop Notifications Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Notifications color="primary" sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Desktop Notifications
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get notified when new emails arrive
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={settings.enableDesktopNotifications ?? true}
              onChange={(e) => handleSettingChange('enableDesktopNotifications', e.target.checked)}
            />
          </Box>

          {settings.enableDesktopNotifications && (
            <>
              <Divider sx={{ my: 2 }} />
              
              <Button 
                variant="outlined" 
                size="small"
                onClick={async () => {
                  const success = await testNotification();
                  if (!success) {
                    setSnackbar({ open: true, message: 'Please allow notifications in browser settings', severity: 'info' });
                  }
                }}
              >
                Test Notification
              </Button>
            </>
          )}
        </Paper>

        {settings.enableDesktopNotifications && (
          <>
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Notification Content
              </Typography>

              <RadioGroup
                value={settings.notificationContent || 'full'}
                onChange={(e) => handleSettingChange('notificationContent', e.target.value)}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">Show sender, subject, and preview</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Full notification with email details
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="minimal"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">Show sender and subject only</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Hide email preview text
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="count"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">Show count only</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Just show "X new emails" - best for privacy
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Notification Actions
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notificationActions ?? true}
                      onChange={(e) => handleSettingChange('notificationActions', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Show action buttons</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add Reply, Archive, Delete buttons to notifications
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.groupNotifications ?? true}
                      onChange={(e) => handleSettingChange('groupNotifications', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Group notifications</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Combine multiple notifications into one
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Badge Count
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showBadgeCount ?? true}
                    onChange={(e) => handleSettingChange('showBadgeCount', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Show unread count on icon</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Display number badge on the app icon
                    </Typography>
                  </Box>
                }
              />
            </Paper>
          </>
        )}
      </TabPanel>

      {/* Folder Notifications Tab */}
      <TabPanel value={tabValue} index={1}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Choose which folders send you notifications when new emails arrive.
        </Alert>

        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <List>
            {folderNotifications.map((folder, index) => (
              <React.Fragment key={folder.id}>
                <ListItem
                  key={folder.id}
                  sx={{ py: 2 }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">Sound</Typography>
                        <Switch
                          size="small"
                          checked={folder.sound}
                          onChange={() => handleFolderNotificationToggle(folder.id, 'sound')}
                          disabled={!folder.enabled}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">Notify</Typography>
                        <Switch
                          checked={folder.enabled}
                          onChange={() => handleFolderNotificationToggle(folder.id, 'enabled')}
                        />
                      </Box>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    {folder.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={folder.name}
                    secondary={
                      folder.enabled 
                        ? (folder.sound ? 'Notifications with sound' : 'Silent notifications')
                        : 'Notifications disabled'
                    }
                  />
                </ListItem>
                {index < folderNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, mt: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            VIP Contacts
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.vipNotifications ?? true}
                onChange={(e) => handleSettingChange('vipNotifications', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Always notify for VIP contacts
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Get notifications from starred contacts even during quiet hours
                </Typography>
              </Box>
            }
          />
        </Paper>
      </TabPanel>

      {/* Sounds Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <VolumeUp color="primary" sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Notification Sound
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Play a sound when new emails arrive
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={settings.soundEnabled ?? true}
              onChange={(e) => handleSettingChange('soundEnabled', e.target.checked)}
            />
          </Box>
        </Paper>

        {settings.soundEnabled && (
          <>
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Sound Selection
              </Typography>

              <List>
                {notificationSounds.map((sound) => (
                  <ListItem
                    key={sound.id}
                    secondaryAction={
                      sound.id !== 'none' ? (
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            playTestSound(sound.id);
                          }}
                        >
                          <PlayArrow fontSize="small" />
                        </IconButton>
                      ) : undefined
                    }
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      cursor: 'pointer',
                      bgcolor: (settings.notificationSound || 'default') === sound.id ? 'primary.light' : 'transparent',
                      border: (settings.notificationSound || 'default') === sound.id ? '1px solid' : '1px solid transparent',
                      borderColor: (settings.notificationSound || 'default') === sound.id ? 'primary.main' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => {
                      handleSettingChange('notificationSound', sound.id);
                      updateNotificationSettings({ soundType: sound.id as any });
                    }}
                  >
                    <ListItemText primary={sound.name} />
                  </ListItem>
                ))}
              </List>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Volume
              </Typography>

              <Box sx={{ px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <VolumeOff color="action" />
                  <Slider
                    value={settings.soundVolume ?? 70}
                    onChange={(_, value) => handleSettingChange('soundVolume', value)}
                    min={0}
                    max={100}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}%`}
                    sx={{ flexGrow: 1 }}
                  />
                  <VolumeUp color="action" />
                </Box>
              </Box>
            </Paper>
          </>
        )}
      </TabPanel>

      {/* Quiet Hours Tab */}
      <TabPanel value={tabValue} index={3}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DoNotDisturb sx={{ color: 'secondary.main', fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Quiet Hours
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pause notifications during certain hours
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={settings.quietHoursEnabled ?? false}
              onChange={(e) => handleSettingChange('quietHoursEnabled', e.target.checked)}
            />
          </Box>
        </Paper>

        {settings.quietHoursEnabled && (
          <>
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Schedule
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  label="Start time"
                  type="time"
                  value={settings.quietHoursStart || '22:00'}
                  onChange={(e) => handleSettingChange('quietHoursStart', e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
                <Typography variant="body2" color="text.secondary">to</Typography>
                <TextField
                  label="End time"
                  type="time"
                  value={settings.quietHoursEnd || '07:00'}
                  onChange={(e) => handleSettingChange('quietHoursEnd', e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                Notifications will be paused from {settings.quietHoursStart || '22:00'} to {settings.quietHoursEnd || '07:00'}
              </Alert>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Quick Presets
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label="Night (10PM - 7AM)"
                  onClick={async () => { try { await updateSettings({ quietHoursStart: '22:00', quietHoursEnd: '07:00' }); setSnackbar({ open: true, message: 'Settings saved', severity: 'success' }); } catch { setSnackbar({ open: true, message: 'Failed to save', severity: 'error' }); } }}
                  variant={(settings.quietHoursStart || '22:00') === '22:00' && (settings.quietHoursEnd || '07:00') === '07:00' ? 'filled' : 'outlined'}
                  color="primary"
                />
                <Chip
                  label="Sleeping (11PM - 8AM)"
                  onClick={async () => { try { await updateSettings({ quietHoursStart: '23:00', quietHoursEnd: '08:00' }); setSnackbar({ open: true, message: 'Settings saved', severity: 'success' }); } catch { setSnackbar({ open: true, message: 'Failed to save', severity: 'error' }); } }}
                  variant={(settings.quietHoursStart) === '23:00' && (settings.quietHoursEnd) === '08:00' ? 'filled' : 'outlined'}
                  color="primary"
                />
                <Chip
                  label="Work hours only (6PM - 9AM)"
                  onClick={async () => { try { await updateSettings({ quietHoursStart: '18:00', quietHoursEnd: '09:00' }); setSnackbar({ open: true, message: 'Settings saved', severity: 'success' }); } catch { setSnackbar({ open: true, message: 'Failed to save', severity: 'error' }); } }}
                  variant={(settings.quietHoursStart) === '18:00' && (settings.quietHoursEnd) === '09:00' ? 'filled' : 'outlined'}
                  color="primary"
                />
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Exceptions
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.quietHoursAllowVip ?? true}
                      onChange={(e) => handleSettingChange('quietHoursAllowVip', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Allow VIP contacts</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Starred contacts can still notify during quiet hours
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.quietHoursAllowUrgent ?? false}
                      onChange={(e) => handleSettingChange('quietHoursAllowUrgent', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Allow urgent emails</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Emails marked as high priority can bypass quiet hours
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Paper>
          </>
        )}
      </TabPanel>
    </Box>
  );
};

export default NotificationSettings;
